import { Box, Card, Divider, Grid, Loader, Stack, Text, Title } from "@mantine/core";
import { useMsal } from "@azure/msal-react";
import { getHeaders, Source } from "../../api";
import { useLogin, getToken, isInternalEmployee } from "../../authConfig";
import { useEffect, useState } from "react";
import { getFileExtension } from "./getFileExtenstion";
import { PdfViewer } from "../pdf-viewer";
import { VideoViewer } from "../video-viewer";
import { CitationImage } from "../citation-image";
import { CitationMarkdownViewer } from "../citation-markdown-viewer";
import { MdOpenInNew } from "react-icons/md";
import { PlaybookViewer } from "../playbook-viewer";
import { t } from "i18next";

export interface SourceViewerProps {
    source?: Source;
    anchor?: string;
}

type Asset = AlgoliaAsset | PublicationInternalAsset | PublicationExternalAsset | IntellumAsset | VideoAudioAsset;

interface AlgoliaAsset {
    kind: "algolia";
    source: Source;
    url: string;
}

interface PublicationInternalAsset {
    kind: "publication-internal";
    source: Source;
    url: string;
}

interface PublicationExternalAsset {
    kind: "publication-external";
    source: Source;
    publicationUrl: string;
}

interface IntellumAsset {
    kind: "intellum";
    source: Source;
    url: string;
}

interface VideoAudioAsset {
    kind: "video-audio";
    source: Source;
    mediaType: "video" | "audio";
    url: string;
}

export const SourceViewer = ({ source, anchor }: SourceViewerProps) => {
    const client = useLogin ? useMsal().instance : undefined;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [citationSrc, setCitationSrc] = useState<string | null>(null);
    const [asset, setAsset] = useState<Asset | null>(null);

    const fetchAsset = async (path: string) => {
        try {
            setIsLoading(true);
            const token = client ? await getToken(client) : undefined;
            const response = await fetch(path, {
                method: "GET",
                headers: await getHeaders(token)
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch citation: ${response.statusText}`);
            }
            const citationContent = await response.blob();
            let citationObjectUrl = URL.createObjectURL(citationContent);

            if (anchor) {
                citationObjectUrl += "#" + anchor;
            }

            setCitationSrc(citationObjectUrl);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching citation:", error);
            setCitationSrc(null);
        } finally {
            setIsLoading(false); // Set loading state to false
        }
    };

    useEffect(() => {
        if (asset?.kind === "algolia" || asset?.kind === "publication-internal") {
            fetchAsset(asset.url);
        } else {
            setCitationSrc(null);
            setIsLoading(false);
        }
    }, [asset]);

    useEffect(() => {
        const determineHowToShow = async () => {
            if (source?.dataSource == "orbit") {
                if (source.assetUrl && client && (await isInternalEmployee(client))) {
                    setAsset({ kind: "publication-internal", source: source, url: source.assetUrl! });
                } else {
                    setAsset({ kind: "publication-external", source: source, publicationUrl: source.url! });
                }
            } else if (source?.dataSource === "intellum") {
                setAsset({ kind: "intellum", source: source, url: source.url });
            } else if (source?.mediaType === "video" || source?.mediaType == "audio") {
                setAsset({ kind: "video-audio", source: source, mediaType: source?.mediaType, url: source.assetUrl! });
            } else if (source?.assetUrl) {
                setAsset({ kind: "algolia", source: source, url: source.assetUrl! });
            }
        };

        determineHowToShow().catch(console.error);
    }, [source]);

    const renderFileViewer = () => {
        if (isLoading) {
            return <Loader size={16} type="dots" />;
        }

        if (asset?.kind == "video-audio") {
            return <VideoViewer mediaType={asset.mediaType} url={asset.url + (anchor ? "#" + anchor : "")} />;
        }

        if (asset?.kind == "publication-external") {
            return (
                <Box>
                    <a href={asset.publicationUrl} target="_blank" rel="noopener noreferrer">
                        <MdOpenInNew size={14} />
                        View Publication
                    </a>
                </Box>
            );
        }

        if (asset?.kind == "intellum") {
            return (
                <Box>
                    <a href={asset.url} target="_blank" rel="noopener noreferrer">
                        <MdOpenInNew size={14} />
                        View external source
                    </a>
                </Box>
            );
        }

        const fileExtension = getFileExtension(asset?.url || "");

        switch (fileExtension) {
            case "png":
                return <CitationImage src={citationSrc} />;

            case "md":
                return <CitationMarkdownViewer src={source!.url} />;

            case "trs": {
                return <VideoViewer mediaType={source!.mediaType} url={source!.url || ""} />;
            }

            case "pb": {
                return <PlaybookViewer source={source} />;
            }

            default:
                return <PdfViewer file={citationSrc} />;
        }
    };

    return (
        <Box style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ flexGrow: 1 }}>{renderFileViewer()}</div>
            <Grid mt="md">
                <Grid.Col>
                    <Stack gap="sm">
                        <Stack gap="xs">
                            <Text fw={500}>{t("Publication date")}</Text>
                            <Text size="sm">
                                {(() => {
                                    const date = source?.rankingTimestamp ?? source?.lastModifiedOn;
                                    return date != null ? new Date(Date.parse(date)).toLocaleDateString() : "-";
                                })()}
                            </Text>

                            {source!.referenceNumber && (
                                <>
                                    {" "}
                                    <Text fw={500}>{t("Reference Number")}</Text>
                                    <Text size="sm">{source!.referenceNumber}</Text>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Grid.Col>
            </Grid>
        </Box>
    );
};
